import styles from "./dynamicForm.module.css";
import { useCRUD } from "../../../api/crud";
import { useApiRequest } from "../../../api/axios";
import axios from "../../../api/axios";
import { JSX, useEffect, useState, useRef, useCallback } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { Add, ContentCopy, Delete } from "@mui/icons-material";
import { DRFActions, DRFField, DynamicFieldsConfig, OptionResult, ApiFieldConfigMap, FieldRule, GroupFields, GroupFieldsOrArray, FieldAttributesConfig, NestedFieldLayoutConfig } from "../../../types/dynamicForm";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import CustomDatePicker from "../customDatePicker/customDatePicker.component";
import CustomDateTimePicker from "../customDateTimePicker/customDateTimePicker.component";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { PickerValue } from "@mui/x-date-pickers/internals";
import CustomCircularProgress from "../customCircularProgress/customCircularProgress.component";
import FileDropZoneField from "../fileDropZoneField/fileDropZoneField.component";
import { DECIMAL_INPUT_REGEX } from "../../../utils";

interface DynamicFormProps {
  url: string;
  onSuccess?: (data?: any) => void;
  dynamicFields?: DynamicFieldsConfig;
  apiFields?: ApiFieldConfigMap;
  /** When set, options for these api fields are taken from here instead of fetching from URL. No search/fetch is performed for overridden fields. */
  apiFieldOptionsOverride?: Record<string, any[]>;
  id?: string;
  initialValues?: Record<string, unknown>;
  groupFields?: GroupFieldsOrArray;
  fieldAttributes?: FieldAttributesConfig;
  nestedFieldLayout?: NestedFieldLayoutConfig;
  endFields?: string[];
  /**
   * Restricts the form to these fields - everything else the endpoint exposes
   * is neither rendered nor submitted. Lets one large resource be edited
   * through several small, focused forms (the endpoint must accept PATCH).
   */
  includeFields?: string[];
}

const OMIT_FIELDS = ["id", "created_at", "updated_at"];

// Files travel as Base64 strings, so a form that saves JSON can carry them.
const readFileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export default function DynamicForm({ url, onSuccess, dynamicFields, apiFields, apiFieldOptionsOverride, id, initialValues, groupFields, fieldAttributes, nestedFieldLayout, endFields, includeFields }: DynamicFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, options, error, loading } = useCRUD(url);
  const { executeRequest } = useApiRequest();
  const [actions, setActions] = useState<DRFActions | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isLoadingInitialValues, setIsLoadingInitialValues] = useState(true);
  const [autocompleteOptions, setAutocompleteOptions] = useState<Record<string, any[]>>({});
  const [autocompleteLoading, setAutocompleteLoading] = useState<Record<string, boolean>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const valueSourceCache = useRef<Record<string, unknown>>({});
  const initialValuesSnapshot = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (!actions) {
      options().then((response: OptionResult) => {
        if (response.data.actions?.POST) {
          setActions(response.data.actions?.POST);
        } else if (response.data.actions?.PUT) {
          setActions(response.data.actions?.PUT);
        }
      });
    }
    const setInitialValues = () => {
      if (actions) {
        if (!id) {
          const defaultInitialValues: Record<string, unknown> = {};
          Object.entries(actions).forEach(([name, field]) => {
            if (field.initial !== null) defaultInitialValues[name] = field.initial;
          });
          // Merge with provided initialValues (provided values take precedence)
          const mergedInitialValues = { ...defaultInitialValues, ...initialValues };
          initialValuesSnapshot.current = mergedInitialValues;
          setFormData(mergedInitialValues);
          setIsLoadingInitialValues(false);
        } else {
          get(id).then((response) => {
            const values: Record<string, unknown> = {};
            Object.entries(response.data).forEach(([name, value]) => {
              if (value && typeof value === "object" && !Array.isArray(value)) {
                Object.entries(value).forEach(([subKey, subValue]) => {
                  if (
                    !OMIT_FIELDS.includes(subKey) &&
                    subValue
                  ) {
                    values[subKey] = subValue;
                  }
                });
              } else if (Array.isArray(value)) {
                // Handle arrays of nested objects - normalize nested field values to IDs
                const normalizedArray = value.map((item) => {
                  if (item && typeof item === "object") {
                    const normalizedItem: Record<string, unknown> = {};
                    Object.entries(item).forEach(([key, val]) => {
                      // If the value is an object with an 'id' property, extract just the ID
                      if (val && typeof val === "object" && "id" in val) {
                        normalizedItem[key] = (val as { id: unknown }).id;
                      } else if (Array.isArray(val)) {
                        normalizedItem[key] = val.map((arrayItem) => {
                          if (arrayItem && typeof arrayItem === "object" && "id" in (arrayItem as Record<string, unknown>)) {
                            return (arrayItem as { id: unknown }).id;
                          }
                          return arrayItem;
                        });
                      } else {
                        normalizedItem[key] = val;
                      }
                    });
                    return normalizedItem;
                  }
                  return item;
                });
                values[name] = normalizedArray;
              } else {
                values[name] = value;
              }
            });
            // Merge with provided initialValues (provided values take precedence)
            const mergedValues = { ...values, ...initialValues };
            initialValuesSnapshot.current = mergedValues;
            setFormData(mergedValues);
            setIsLoadingInitialValues(false);
          }).catch(() => {
            setIsLoadingInitialValues(false);
          });
        }
      }
    };
    setInitialValues();
  }, [options, actions, get, id, initialValues]);

  const fetchAutocompleteOptions = useCallback(
    async (fieldName: string, search?: string | string[]) => {
      if (!apiFields || !apiFields[fieldName]) return;

      // Normalize search value: text query (string) vs. multiple IDs (string[])
      const searchStr = typeof search === 'string' ? search : undefined;
      const searchIds = Array.isArray(search) ? search : undefined;

      // Clear previous timeout for this field
      if (debounceTimers.current[fieldName]) {
        clearTimeout(debounceTimers.current[fieldName]);
      }

      // Only enforce minimum character requirement if search text is provided
      if (searchStr !== undefined && searchStr !== "" && searchStr.trim().length < 2) {
        setAutocompleteLoading((prev) => ({
          ...prev,
          [fieldName]: false,
        }));
        return;
      }

      // Set debounce timer - wait until user stops typing
      debounceTimers.current[fieldName] = setTimeout(async () => {
        setAutocompleteLoading((prev) => ({
          ...prev,
          [fieldName]: true,
        }));

        try {
          const config = apiFields[fieldName];
          const params = new URLSearchParams();

          if (config.queryParams) {
            Object.entries(config.queryParams).forEach(([paramKey, paramConfig]) => {
              const rawValue = typeof paramConfig === "string"
                ? paramConfig
                : formData[paramConfig.fromField];

              const normalizedValue = rawValue && typeof rawValue === "object" && "id" in (rawValue as Record<string, unknown>)
                ? (rawValue as { id?: unknown }).id
                : rawValue;

              if (normalizedValue === null || normalizedValue === undefined || normalizedValue === "") {
                return;
              }

              params.append(paramKey, String(normalizedValue));
            });
          }

          // If we have IDs to fetch (for multiple fields with initial values)
          if (searchIds && searchIds.length > 0) {
            const normalizedIds = searchIds
              .map((searchId) => {
                if (searchId && typeof searchId === "object" && "id" in (searchId as Record<string, unknown>)) {
                  return (searchId as { id?: unknown }).id;
                }
                return searchId;
              })
              .filter((searchId): searchId is string | number => typeof searchId === "string" || typeof searchId === "number");

            if (normalizedIds.length === 0) {
              setAutocompleteOptions((prev) => ({
                ...prev,
                [fieldName]: [],
              }));
              return;
            }

            // Fetch each ID individually to get their details
            const results = await Promise.all(
              normalizedIds.map(id =>
                executeRequest(() => axios.get(`${config.url}${id}/`))
                  .then(res => res.data)
                  .catch(() => null)
              )
            );
            const validResults = results.filter(Boolean);
            setAutocompleteOptions((prev) => ({
              ...prev,
              [fieldName]: validResults,
            }));
          } else {
            // Regular search with search text
            if (searchStr && searchStr.trim()) {
              params.append("search", searchStr.trim());
            }
            const url = `${config.url}${params.toString() ? "?" + params.toString() : ""}`;
            const { data } = await executeRequest(() => axios.get(url));
            if (data.results) {
              setAutocompleteOptions((prev) => ({
                ...prev,
                [fieldName]: Array.isArray(data?.results) ? data.results : data,
              }));
            }
          }
        } catch (err) {
          console.error(`Failed to fetch autocomplete options for ${fieldName}:`, err);
        } finally {
          setAutocompleteLoading((prev) => ({
            ...prev,
            [fieldName]: false,
          }));
        }
      }, 300); // Wait 300ms after user stops typing
    },
    [apiFields, executeRequest, formData]
  );

  // Cleanup debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Fetch autocomplete options for fields with initial values
  useEffect(() => {
    if (!apiFields || !formData || isLoadingInitialValues) return;

    Object.entries(apiFields).forEach(([fieldName]) => {
      if (apiFieldOptionsOverride?.[fieldName]) return; // use override only, no fetch
      const hasOptions = autocompleteOptions[fieldName]?.length > 0;

      // Check if this is a nested field (e.g., "services.code")
      if (fieldName.includes(".")) {
        const [parentField, childField] = fieldName.split(".");
        const parentValue = formData[parentField];

        // If parent is an array, check if any row has a value for this child field
        if (Array.isArray(parentValue)) {
          const hasAnyValue = parentValue.some((row) => {
            const value = (row as Record<string, unknown>)?.[childField];
            // Value could be an ID or an object with an id property
            const hasValue = value !== null && value !== undefined && value !== "";
            return hasValue;
          });

          if (hasAnyValue && !hasOptions) {
            // Get the first non-empty value to use as search parameter
            const searchValue = parentValue.find((row) => {
              const value = (row as Record<string, unknown>)?.[childField];
              return value !== null && value !== undefined && value !== "";
            })?.[childField] as string | undefined;
            if (searchValue !== undefined && searchValue !== null && searchValue !== "") {
              // For initial values, fetch by ID to avoid conflicts with extra query params (e.g. client filter)
              fetchAutocompleteOptions(fieldName, [searchValue]);
            }
          }
        }
      } else {
        // Top-level field
        const initialValue = formData[fieldName];
        const config = apiFields[fieldName];

        // If there's an initial value and we don't have options yet, fetch them
        if (initialValue && !hasOptions) {
          // For multiple fields, pass the array of IDs directly
          if (config?.multiple && Array.isArray(initialValue)) {
            fetchAutocompleteOptions(fieldName, initialValue);
          } else {
            // For single fields with initial value, fetch by ID to avoid search/query-param conflicts
            fetchAutocompleteOptions(fieldName, [initialValue as string]);
          }
        }
      }
    });
  }, [isLoadingInitialValues, apiFields, apiFieldOptionsOverride, formData, autocompleteOptions, fetchAutocompleteOptions]);

  const handleNestedChange = (
    parent: string,
    index: number,
    child: string,
    value: unknown
  ) => {
    setFormData((prev) => {
      const existing = Array.isArray(prev[parent])
        ? [...(prev[parent] as unknown[])]
        : [];
      existing[index] = {
        ...(existing[index] as Record<string, unknown>),
        [child]: value,
      };
      return { ...prev, [parent]: existing };
    });
  };

  const handleAddNestedRow = (fieldName: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...((prev[fieldName] as unknown[]) || []), {}],
    }));
  };

  const handleRemoveNestedRow = (fieldName: string, index: number) => {
    setFormData((prev) => {
      const existing = Array.isArray(prev[fieldName])
        ? [...(prev[fieldName] as unknown[])]
        : [];
      existing.splice(index, 1);
      return { ...prev, [fieldName]: existing };
    });
  };

  const isOmitted = (name: string) => OMIT_FIELDS.includes(name);
  // A drop zone is much taller than an input, so sharing a row with one
  // leaves the row lopsided - file fields always get a row of their own.
  const takesFullRow = (name: string) =>
    Boolean(fieldAttributes?.[name]?.fullRow || fieldAttributes?.[name]?.file);
  const hasHideRule = (fieldName: string): boolean =>
    !!dynamicFields?.[fieldName]?.some((r) => r.action === "hide");

  const matchesRule = useCallback(
    (rule: { dependsOn: string; value?: unknown }) => {
      const depValue = formData[rule.dependsOn];
      const hasValue = depValue !== null && depValue !== undefined && depValue !== "";

      if (rule.value === "ANY" || rule.value === undefined) {
        return hasValue;
      }

      return depValue === rule.value;
    },
    [formData]
  );

  const resolveValueSourceUrl = (template: string, depValue: unknown) => {
    return template.replace("{id}", encodeURIComponent(String(depValue)));
  };

  useEffect(() => {
    if (!dynamicFields) return;

    Object.entries(dynamicFields).forEach(([targetField, rules]) => {
      rules.forEach((rule: FieldRule) => {
        if (!rule.valueSource) return;

        const depValue = formData[rule.dependsOn];
        const hasDepValue = depValue !== null && depValue !== undefined && depValue !== "";
        const cacheKey = `${targetField}-${rule.dependsOn}`;

        // Clear the target field only when dependent field transitions from having value to empty
        if (!hasDepValue) {
          // Only clear if we had a cached value (meaning it was previously filled)
          if (valueSourceCache.current[cacheKey] !== undefined) {
            delete valueSourceCache.current[cacheKey];
            setFormData((prev) => {
              if (prev[targetField] === null || prev[targetField] === undefined) return prev;
              return { ...prev, [targetField]: null };
            });
          }
          return;
        }

        if (!matchesRule(rule)) return;

        const targetValue = formData[targetField];
        const hasTargetValue = targetValue !== null && targetValue !== undefined && targetValue !== "";
        const initialValueForField = initialValuesSnapshot.current[targetField];
        const initialValuePresent = initialValueForField !== null && initialValueForField !== undefined && initialValueForField !== "";
        const isUsingInitialValue = initialValuePresent && targetValue === initialValueForField;

        // Skip calling valueSource when the field is already populated from initial values
        if (hasTargetValue && isUsingInitialValue) return;

        // Only skip if the dependent value hasn't changed
        if (valueSourceCache.current[cacheKey] === depValue) return;
        valueSourceCache.current[cacheKey] = depValue;

        const url = resolveValueSourceUrl(rule.valueSource, depValue);
        executeRequest(() => axios.get(url))
          .then(({ data }) => {
            const nextValue = data?.value ?? data?.result ?? data;
            setFormData((prev) => {
              // Only set the value if the field doesn't already have a value
              const hasExistingValue = prev[targetField] !== null && prev[targetField] !== undefined && prev[targetField] !== "";
              if (hasExistingValue || prev[targetField] === nextValue) return prev;
              return { ...prev, [targetField]: nextValue };
            });
          })
          .catch((err) => {
            console.error(`Failed to fetch value for ${targetField} from ${url}:`, err);
          });
      });
    });
  }, [dynamicFields, executeRequest, formData, matchesRule]);

  const isDisabled = (fieldName: string): boolean => {
    if (!dynamicFields) return false;
    const rules = dynamicFields[fieldName];
    if (!rules) return false;
    // If any disable rule matches, the field is disabled
    return rules.some((rule) => rule.action === "disable" && matchesRule(rule));
  };

  const renderScalarField = (
    name: string,
    field: DRFField,
    value: unknown,
    onChange: (val: any) => void,
    nestedFieldName?: string
  ) => {
    const getErrorMessage = (fieldName: string, nestedFieldName?: string) => {
      let fieldErrors;
      if (nestedFieldName) {
        const [, index, childName] = fieldName.split("-");
        fieldErrors = error?.[nestedFieldName]?.[index]?.[childName];
      } else {
        fieldErrors = error?.[fieldName];
      }
      return Array.isArray(fieldErrors) && fieldErrors.length > 0
      ? fieldErrors[0]?.message
      : null;
    };
    const errorMessage = getErrorMessage(name, nestedFieldName);
    const errorDiv = errorMessage && (
      <div className={styles.errorMessage}>{errorMessage}</div>
    );

    // Determine the base field key for dynamic rules (strip nested prefixes)
    const baseFieldKey = nestedFieldName ? name.split("-").slice(-1)[0] : name;
    const disabled = isDisabled(baseFieldKey);

    switch (field.type) {
      case "string":
      case "email":
        const attributes = fieldAttributes?.[baseFieldKey] || {};
        if (attributes.file) {
          return (
            <Box>
              <Typography variant="body2" sx={{ color: "var(--textColor)", mb: 0.5 }}>
                {field.label || name}
              </Typography>
              <FileDropZoneField
                accept={attributes.file.accept}
                disabled={disabled}
                onFileSelected={async (file) => {
                  try {
                    onChange(await readFileAsBase64(file));
                    setFileNames((prev) => ({ ...prev, [name]: file.name }));
                  } catch (err) {
                    console.error(`Failed to read file for ${name}:`, err);
                    enqueueSnackbar(t("The file could not be read."), ERROR_SNACKBAR);
                  }
                }}
              />
              <Typography variant="caption" component="div" sx={{ mt: 0.5, color: "text.secondary" }}>
                {fileNames[name] ? t("Selected file: {{name}}", { name: fileNames[name] }) : attributes.helperText}
              </Typography>
              {errorDiv}
            </Box>
          );
        }
        // Layout-only attributes are not TextField props.
        const { fullRow: _fullRow, file: _file, ...textFieldAttributes } = attributes;
        return (
          <Box>
            <TextField
              key={name}
              size="small"
              label={field.label || name}
              value={(value as string) || ""}
              required={field.required}
              onChange={(e) => onChange(e.target.value)}
              fullWidth
              type={field.type === "email" ? "email" : "text"}
              inputProps={
                field.max_length ? { maxLength: field.max_length } : undefined
              }
              disabled={disabled}
              helperText={attributes?.helperText}
              {...textFieldAttributes}
            />
            {errorDiv}
          </Box>
        );

      case "integer":
        const integerAttributes = fieldAttributes?.[baseFieldKey] || {};
        return (
          <Box>
            <TextField
              key={name}
              size="small"
              label={field.label || name}
              value={value === null || value === undefined ? "" : String(value)}
              required={field.required}
              onChange={(e) => {
                const nextValue = e.target.value;
                if (nextValue === "") {
                  onChange(null);
                  return;
                }
                const numValue = parseInt(nextValue, 10);
                if (!isNaN(numValue)) {
                  onChange(numValue);
                }
              }}
              fullWidth
              type="number"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              disabled={disabled}
              helperText={integerAttributes?.helperText}
            />
            {errorDiv}
          </Box>
        );

      case "decimal":
        const decimalAttributes = fieldAttributes?.[baseFieldKey] || {};
        return (
          <Box>
            <TextField
              key={name}
              size="small"
              label={field.label || name}
              value={value === null || value === undefined ? "" : String(value).replace(/\./g, ",")}
              required={field.required}
              onChange={(e) => {
                const nextValue = e.target.value;
                if (nextValue === "") {
                  onChange(null);
                  return;
                }
                if (!DECIMAL_INPUT_REGEX.test(nextValue)) return;
                onChange(nextValue.replace(",", "."));
              }}
              fullWidth
              type="text"
              inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
              InputProps={
                decimalAttributes.suffix
                  ? { endAdornment: <InputAdornment position="end">{decimalAttributes.suffix}</InputAdornment> }
                  : undefined
              }
              disabled={disabled}
              helperText={decimalAttributes?.helperText}
            />
            {errorDiv}
          </Box>
        );

      case "datetime":
        return (
          <Box>
            <CustomDateTimePicker
              key={name}
              label={field.label || name}
              value={value ? dayjs(value as string) : null}
              onChange={(val: Dayjs | null) =>
                onChange(val ? val.format("YYYY-MM-DDTHH:mm:ss") : null)
              }
              ampm={false}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  required: field.required,
                  disabled,
                },
              }}
            />
            {errorDiv}
          </Box>
        );

      case "time":
        return (
          <Box>
            <TimeField
              key={name}
              size="small"
              label={field.label || name}
              value={value ? dayjs(value as PickerValue, "HH:mm") : null}
              required={field.required}
              onChange={(value) => onChange(value?.format("HH:mm"))}
              format="HH:mm"
              fullWidth
              disabled={disabled}
            />
            {errorDiv}
          </Box>
        );

      case "date":
        return (
          <Box>
            <CustomDatePicker
              key={name}
              label={field.label || name}
              value={value ? dayjs(value as string) : null}
              onChange={(value: Dayjs | null) => onChange(value?.format("YYYY-MM-DD"))}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  required: field.required,
                  disabled,
                },
              }}
            />
            {errorDiv}
          </Box>
        );

      case "choice":
        return (
          <FormControl
            key={name}
            fullWidth
            size="small"
            required={field.required}
            disabled={disabled}
          >
            <InputLabel>{field.label || name}</InputLabel>
            <Select
              label={field.label || name}
              value={(value as string) || ""}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
            >
              {field.choices?.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.display_name}
                </MenuItem>
              ))}
            </Select>
            {errorDiv}
          </FormControl>
        );

      case "boolean":
        return (
          <Box>
            <FormControlLabel
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "var(--textColor)",
                },
              }}
              control={
                <Checkbox
                  checked={(value as boolean) || false}
                  onChange={(e) => onChange(e.target.checked)}
                  disabled={disabled}
                />
              }
              label={field.label || name}
            />
            {errorDiv}
          </Box>
        );

      case "field":
        // Render as autocomplete if apiFields is defined and has config for this field
        // Support nested fields via dot-path: parent.child (e.g., services.code)
        if (apiFields) {
          const baseFieldKey = nestedFieldName ? name.split("-").slice(-1)[0] : name;
          const nestedApiKey = nestedFieldName ? `${nestedFieldName}.${baseFieldKey}` : undefined;
          const apiKey = apiFields[name]
            ? name
            : (nestedApiKey && apiFields[nestedApiKey])
              ? nestedApiKey
              : undefined;

          if (apiKey) {
            const fieldConfig = apiFields[apiKey];
            const hasOverride = Boolean(apiFieldOptionsOverride?.[apiKey]);
            const optionsList = hasOverride
              ? (apiFieldOptionsOverride![apiKey] || [])
              : (autocompleteOptions[apiKey] || []);
            const isMultiple = fieldConfig.multiple || false;

          // Handle single vs multiple selection
          let selectedValue: any;
          if (isMultiple) {
            // For multiple mode: convert single ID to array of matching options
            const selectedIds = (Array.isArray(value) ? value : (value ? [value] : []))
              .map((selectedItem) => {
                if (selectedItem && typeof selectedItem === "object" && "id" in (selectedItem as Record<string, unknown>)) {
                  return (selectedItem as { id?: unknown }).id;
                }
                return selectedItem;
              })
              .filter((selectedItem): selectedItem is string | number => typeof selectedItem === "string" || typeof selectedItem === "number");
            selectedValue = optionsList.filter((option) =>
              selectedIds.includes(option?.id)
            );
          } else {
            // For single mode: find the option object by ID
            // Normalize value: if it's an object with an id, extract the id
            const normalizedValue = value && typeof value === "object" && "id" in value
              ? (value as { id: unknown }).id
              : value;
            selectedValue = optionsList.find((option) => option?.id === normalizedValue) || null;
          }

          return (
            <Box>
              <Autocomplete
                key={name}
                size={"small"}
                multiple={isMultiple}
                options={optionsList}
                loading={!hasOverride && (autocompleteLoading[apiKey] || false)}
                loadingText={t("Loading...")}
                onOpen={() => {
                  if (hasOverride) return;
                  setAutocompleteLoading((prev) => ({
                    ...prev,
                    [apiKey]: true,
                  }));
                  fetchAutocompleteOptions(apiKey);
                }}
                onInputChange={(_, inputValue, reason) => {
                  if (hasOverride) return;
                  // Only fetch when user is actively typing, not when displaying selected value
                  if (reason !== 'input') return;

                  if (inputValue && inputValue.length > 0) {
                    fetchAutocompleteOptions(apiKey, inputValue);
                  } else if (inputValue === "") {
                    // When user clears input, fetch initial options again
                    fetchAutocompleteOptions(apiKey);
                  }
                }}
                onChange={(_, newValue) => {
                  if (isMultiple) {
                    // For multiple: extract IDs from selected options
                    const selectedIds = Array.isArray(newValue)
                      ? newValue.map((item) => (item ? item.id : null)).filter(Boolean)
                      : [];
                    onChange(selectedIds);
                  } else {
                    // For single: extract ID from selected option
                    onChange(newValue ? newValue.id : null);
                  }
                }}
                value={selectedValue}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : option[fieldConfig.field] || ""
                }
                isOptionEqualToValue={(option, value) => {
                  const optionId = typeof option === "string" ? option : option?.id;
                  const valueId = typeof value === "string" ? value : value?.id;
                  return optionId === valueId;
                }}
                filterOptions={hasOverride ? (options, state) => {
                  const input = (state.inputValue || "").trim().toLowerCase();
                  if (!input) return options;
                  const labelKey = fieldConfig.field;
                  return options.filter((opt) =>
                    (typeof opt === "string" ? opt : (opt?.[labelKey] ?? "")).toString().toLowerCase().includes(input)
                  );
                } : (x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label || name}
                    required={field.required}
                    fullWidth
                    disabled={disabled}
                  />
                )}
                disabled={disabled}
                noOptionsText={t("No options")}
              />
              {errorDiv}
            </Box>
          );
          }
        }
        return null;

      default:
        return null;
    }
  };

  const renderNestedField = (name: string, field: DRFField) => {
    const rawValue = formData[name];
    const rows: Record<string, unknown>[] = Array.isArray(rawValue)
      ? rawValue
      : rawValue && typeof rawValue === "object"
      ? [rawValue as Record<string, unknown>]
      : [];

    const childFields = field.children || field.child?.children || {};
    const childEntries = Object.entries(childFields);
    const configuredRows = nestedFieldLayout?.[name] || [];
    const hasConfiguredRows = configuredRows.length > 0;

    const renderedChildNames = new Set(configuredRows.flat());
    const remainingChildNames = childEntries
      .map(([childName]) => childName)
      .filter((childName) => !renderedChildNames.has(childName));

    const nestedRowsToRender = hasConfiguredRows
      ? [
          ...configuredRows,
          ...(remainingChildNames.length > 0 ? [remainingChildNames] : []),
        ]
      : [childEntries.map(([childName]) => childName)];

    const renderNestedChild = (
      childName: string,
      childField: DRFField,
      row: Record<string, unknown>,
      index: number
    ) => {
      const value = row[childName] ?? "";

      return (
        (childField.read_only || isOmitted(childName)) ? null : (
          <Box
            key={`${name}-${index}-${childName}`}
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 0" },
              minWidth: 0,
            }}
          >
            {renderScalarField(
              `${name}-${index}-${childName}`,
              childField,
              value,
              (val) =>
                handleNestedChange(name, index, childName, val),
              name
            )}
          </Box>
        )
      );
    };

    return (
      <Box
        key={name}
        sx={{ p: 2, border: "1px solid #ccc", borderRadius: 2 }}
      >
        <Typography variant="h6" gutterBottom sx={{ color: "var(--textColor)" }}>
          {field.label || name}
        </Typography>

        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #eee",
              borderRadius: 1,
              p: 2,
              mb: 1,
              backgroundColor: "var(--componentBgColor)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                {field.label || name} #{index + 1}
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveNestedRow(name, index)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                {nestedRowsToRender.map((rowFieldNames, rowIndex) => (
                  <Box
                    key={`${name}-${index}-row-${rowIndex}`}
                    sx={{
                      display: "flex",
                      gap: 1,
                      width: "100%",
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    {rowFieldNames.map((childName) => {
                      const childField = childFields[childName] as DRFField | undefined;
                      if (!childField) return null;
                      return renderNestedChild(childName, childField, row, index);
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
        ))}

        <Button
          startIcon={<Add />}
          variant="outlined"
          size="small"
          onClick={() => handleAddNestedRow(name)}
        >
          {t("Add")} {field.label || name}
        </Button>
      </Box>
    );
  };

  const renderField = (name: string, field: DRFField): JSX.Element | null => {
    if (field.read_only || isOmitted(name)) return null;

    // Check if this is an API field with autocomplete
    if (field.type === "field" && apiFields && apiFields[name]) {
      return renderScalarField(name, field, formData[name], (val) =>
        setFormData((prev) => ({ ...prev, [name]: val }))
      );
    }

    // Otherwise render as nested field if type is "field"
    if (field.type === "field") return renderNestedField(name, field);

    return renderScalarField(name, field, formData[name], (val) =>
      setFormData((prev) => ({ ...prev, [name]: val }))
    );
  };

  // Strip empty strings (including nested) so we don't send them in requests
  const pruneEmptyStrings = (value: unknown): unknown => {
    if (value === null) return value;
    if (value === "") return undefined;

    if (Array.isArray(value)) {
      const cleanedArray = value
        .map((item) => pruneEmptyStrings(item))
        .filter((item) => item !== undefined);
      return cleanedArray.length > 0 ? cleanedArray : undefined;
    }

    if (value && typeof value === "object") {
      const cleanedObject = Object.entries(value as Record<string, unknown>).reduce(
        (acc, [key, val]) => {
          const cleanedVal = pruneEmptyStrings(val);
          if (cleanedVal !== undefined) {
            acc[key] = cleanedVal;
          }
          return acc;
        },
        {} as Record<string, unknown>
      );
      return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
    }

    return value;
  };

  // `id` is how `update` addresses the record, not something the form edits,
  // so a restricted form still has to submit it.
  const isIncluded = (fieldName: string): boolean =>
    !includeFields || fieldName === "id" || includeFields.includes(fieldName);

  const shouldShow = (fieldName: string): boolean => {
    if (!isIncluded(fieldName)) return false;
    if (!dynamicFields) return true;
    const rules = dynamicFields[fieldName];
    if (!rules) return true;

    // If field has 'hide' rules, never show it (value still submitted)
    if (rules.some((r) => r.action === "hide")) return false;

    // If field has 'disable' rules, always show it (it will be disabled by isDisabled function)
    const hasDisableRules = rules.some((r) => r.action === "disable");
    if (hasDisableRules) return true;

    // Treat rules without explicit action as 'show' visibility rules
    const showRules = rules.filter((r) => r.action === "show" || r.action === undefined);
    if (showRules.length === 0) return true;

    // If any 'show' rule matches, show the field
    return showRules.some((rule) => matchesRule(rule));
  };

  if (!actions || isLoadingInitialValues) {
    return <CustomCircularProgress />;
  }

  const groups: GroupFields[] = groupFields
    ? Array.isArray(groupFields)
      ? groupFields
      : [groupFields]
    : [];
  const groupedFieldNames = groups.flatMap((g) => g.fields);

  const regularFields = Object.entries(actions).filter(
    ([name, field]) => {
      if (field.type === "field" && apiFields && apiFields[name]) {
        return shouldShow(name) && !groupedFieldNames.includes(name);
      }
      return field.type !== "field" && field.type !== "boolean" && shouldShow(name) && !groupedFieldNames.includes(name);
    }
  );
  const endFieldsSet = new Set(endFields || []);
  const orderedRegularFields = [
    ...regularFields.filter(([name]) => !endFieldsSet.has(name)),
    ...regularFields.filter(([name]) => endFieldsSet.has(name)),
  ];
  const booleanFields = Object.entries(actions).filter(
    ([name, field]) => field.type === "boolean" && shouldShow(name) && !groupedFieldNames.includes(name)
  );
  const nestedFields = Object.entries(actions).filter(
    ([name, field]) => {
      if (field.type === "field" && apiFields && apiFields[name]) {
        return false;
      }
      return field.type === "field" && !field.read_only && shouldShow(name) && !groupedFieldNames.includes(name);
    }
  );
  const getFieldsForGroup = (group: GroupFields) => {
    const names = group.fields;
    const regular = Object.entries(actions).filter(
      ([name, field]) => {
        if (!names.includes(name) || !shouldShow(name)) return false;
        if (field.type === "field" && apiFields && apiFields[name]) return true;
        return field.type !== "field";
      }
    );
    const nested = Object.entries(actions).filter(
      ([name, field]) => {
        if (!names.includes(name) || !shouldShow(name)) return false;
        if (field.type === "field" && apiFields && apiFields[name]) return false;
        return field.type === "field";
      }
    );
    return { regular, nested };
  };

  return (
    <>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {orderedRegularFields.map(([name, field]) => {
        const rendered = renderField(name, field);
        return rendered ? (
          <Box
            key={name}
            sx={{
              flex: { xs: "1 1 100%", sm: takesFullRow(name) ? "1 1 100%" : "1 1 45%" },
              maxWidth: { xs: "100%", sm: takesFullRow(name) ? "100%" : "calc(50% - 8px)" },
            }}
          >
            {rendered}
          </Box>
        ) : null;
      })}
      {booleanFields.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {booleanFields.map(([name, field]) => {
            const rendered = renderField(name, field);
            return rendered ? (
              <Box
                key={name}
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}
              >
                {rendered}
              </Box>
            ) : null;
          })}
        </Box>
      )}
      {groups.length > 0 && groups.map((group) => {
        const { regular: groupedRegularFields, nested: groupedNestedFields } = getFieldsForGroup(group);
        if (groupedRegularFields.length === 0 && groupedNestedFields.length === 0) return null;
        return (
          <Box
            key={group.title}
            sx={{
              width: "100%",
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 2,
              mt: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: "var(--textColor)" }}>
                {group.title}
              </Typography>
              {group.action && (() => {
                const { label, getValues, disabled, disabledHint } = group.action;
                const isDisabled = Boolean(disabled?.(formData));
                return (
                  <Tooltip title={isDisabled ? disabledHint ?? "" : ""}>
                    {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopy />}
                        disabled={isDisabled}
                        onClick={() => setFormData((prev) => ({ ...prev, ...getValues(prev) }))}
                      >
                        {label}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {groupedRegularFields.map(([name, field]) => {
                const rendered = renderField(name, field);
                return rendered ? (
                  <Box
                    key={name}
                    sx={{ flex: { xs: "1 1 100%", sm: takesFullRow(name) ? "1 1 100%" : "1 1 45%" } }}
                  >
                    {rendered}
                  </Box>
                ) : null;
              })}
            </Box>
            {groupedNestedFields.length > 0 && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                {groupedNestedFields.map(([name, field]) => {
                  const rendered = renderField(name, field);
                  return rendered ? (
                    <Box key={name} sx={{ width: "100%" }}>
                      {rendered}
                    </Box>
                  ) : null;
                })}
              </Box>
            )}
          </Box>
        );
      })}
      {nestedFields.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: "100%",
            flexDirection: "column",
          }}
        >
          {nestedFields.map(([name, field]) => {
            const rendered = renderField(name, field);
            return rendered ? (
              <Box key={name} sx={{ flex: "1 1 45%" }}>
                {rendered}
              </Box>
            ) : null;
          })}
        </Box>
      )}
      <Box
        sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}
      >
        <Button
          variant="contained"
          color="success"
          loading={loading}
          onClick={() => {
            const filteredData: Record<string, unknown> = {};

            Object.entries(formData).forEach(([name, value]) => {
              // Check if field exists in actions and should be shown (or has hide rule → still submit)
              const fieldExists = actions && name in actions;
              if (fieldExists && isIncluded(name) && (shouldShow(name) || hasHideRule(name))) {
                const cleanedValue = pruneEmptyStrings(value);
                if (cleanedValue !== undefined) {
                  filteredData[name] = cleanedValue;
                }
              }
            });

            (id ? update : create)(filteredData).then((response) => {
              if (response?.success) {
                enqueueSnackbar(
                  t("Form was succesfully processed."),
                  SUCCESS_SNACKBAR
                );
                onSuccess?.(response.data);
              } else {
                const failureMessage =
                  (response?.errorData &&
                  typeof response.errorData === "string"
                    ? response.errorData
                    : response?.errorData?.message) ||
                  (response?.data && typeof response.data === "string"
                    ? response.data
                    : response?.data?.message) ||
                  (typeof error === "string" ? error : error?.message) ||
                  t("There was an error, please review the form.");

                enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
              }
            });
          }}
        >
          {t("Submit")}
        </Button>
      </Box>
    </Box>

    </>
  );
}
